import React, { useState, useEffect } from "react";
import axios from "axios";
import { Col, Button, Form } from "react-bootstrap";
import Swal from "sweetalert2";
import formatToWIB from "../../shared/dateUtils";
import { fetchAllowedEmailsAndUsers } from "../utils/fetchData";
import {
  validateEmail,
  sanitizeInput,
  isValidEmail,
} from "../utils/validationUtils";
import ModalAdmin from "../component/ModalAdmin";
import TableAdmin from "../component/TableAdmin";

const ManageUser = () => {
  const [combinedData, setCombinedData] = useState([]);
  const [loading, setLoading] = useState(true); // Add loading state
  const [showModal, setShowModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [newEmail, setNewEmail] = useState("");
  const [editData, setEditData] = useState({
    id: "",
    email: "",
    uid: "",
    statusTes: "",
  });
  const [originalData, setOriginalData] = useState({});
  const [validated, setValidated] = useState(false);
  const [addEmailError, setAddEmailError] = useState("");
  const [editEmailError, setEditEmailError] = useState("");

  useEffect(() => {
    const handleDataChange = fetchAllowedEmailsAndUsers((data) => {
      setCombinedData(data);
      setLoading(false); // Set loading to false once data is fetched
    });
    const subscribe = handleDataChange();
    return () => {
      if (subscribe) subscribe();
    };
  }, []);

  const clearForm = () => {
    setNewEmail("");
    setEditData({ id: "", email: "", uid: "", statusTes: "Pending Test" });
    setOriginalData({});
    setValidated(false);
    setAddEmailError("");
    setEditEmailError("");
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setShowEditModal(false);
    clearForm();
  };

  const handleEmailChange = (setEmail, setError, email) => {
    const sanitizedEmail = sanitizeInput(email);
    setEmail(sanitizedEmail);
    const error = validateEmail(sanitizedEmail);
    setError(error);
    setValidated(error === "" && isValidEmail(sanitizedEmail));
  };

  const handleAddUser = async (event) => {
    event.preventDefault();
    setValidated(true);

    const sanitizedEmail = sanitizeInput(newEmail);
    const validationMessage = validateEmail(sanitizedEmail);
    if (validationMessage) {
      setAddEmailError(validationMessage);
      return;
    } else {
      setAddEmailError("");
    }

    const currentDate = formatToWIB(new Date());
    const newUser = {
      email: sanitizedEmail,
      created_at: currentDate,
      status: "Pending Login",
      statusTes: "Pending Test",
    };

    try {
      await axios.post("https://psikotes-server-upj.vercel.app/allowed-emails", newUser);
      const handleDataChange = fetchAllowedEmailsAndUsers(setCombinedData);
      handleDataChange();
      handleCloseModal();
      Swal.fire({
        icon: "success",
        title: "User Added",
        text: "The user has been successfully added.",
        timerProgressBar: true,
        timer: 1000,
        showConfirmButton: true,
      });
    } catch (error) {
      clearForm();
      if (error.response && error.response.status === 400) {
        Swal.fire({
          icon: "info",
          title: "Duplicate Email",
          text: "The email address is already in use. Please use a different email.",
        });
      } else {
        console.error("Error adding new user:", error);
      }
    }
  };

  const handleEditUser = (data) => {
    setEditData({
      id: data.id,
      email: data.email,
      uid: data.uid,
      statusTes: data.statusTes || "N/A",
    });
    setOriginalData({
      email: data.email,
      statusTes: data.statusTes || "N/A",
    });
    setShowEditModal(true);
  };

  const handleSaveEdit = async (event) => {
    event.preventDefault();
    setValidated(true);

    const sanitizedEmail = sanitizeInput(editData.email);
    const validationMessage = validateEmail(sanitizedEmail);
    if (validationMessage) {
      setEditEmailError(validationMessage);
      return;
    } else {
      setEditEmailError("");
    }

    const updateAllowedEmails = editData.email !== originalData.email;
    const updateUsers = editData.statusTes !== originalData.statusTes;

    try {
      if (updateAllowedEmails) {
        const existingEmails = combinedData.map((user) => user.email);
        if (existingEmails.includes(sanitizedEmail)) {
          Swal.fire({
            icon: "info",
            title: "Duplicate Email",
            text: "The email address is already in use. Please use a different email.",
          }).then(() => {
            handleCloseModal();
          });
          return;
        }
        await axios.put(`https://psikotes-server-upj.vercel.app/allowed-emails/${editData.id}`, {
          email: sanitizedEmail,
        });
      }

      if (updateUsers) {
        await axios.put(`https://psikotes-server-upj.vercel.app/users/${editData.uid}`, {
          statusTes: editData.statusTes,
        });
      }

      const handleDataChange = fetchAllowedEmailsAndUsers(setCombinedData);
      handleDataChange();
      handleCloseModal();

      Swal.fire({
        icon: "success",
        title: "User Edited",
        text: "The user has been successfully edited.",
        timerProgressBar: true,
        timer: 1000,
        showConfirmButton: true,
      });
    } catch (error) {
      console.error("Error editing user:", error);
    }
  };

  const handleDeleteUser = async (data) => {
    Swal.fire({
      title: "Are you sure?",
      text: "Do you really want to delete this user?",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#d33",
      confirmButtonText: "Yes, delete it!",
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          await axios.delete(`https://psikotes-server-upj.vercel.app/allowed-emails/${data.id}`);
          const handleDataChange = fetchAllowedEmailsAndUsers(setCombinedData);
          handleDataChange();
          Swal.fire({
            icon: "success",
            title: "Deleted!",
            text: "The user has been deleted.",
            timerProgressBar: true,
            timer: 1000,
            showConfirmButton: true,
          });
        } catch (error) {
          console.error("Error deleting user:", error);
        }
      }
    });
  };

  const columns = [
    { key: "id", label: "No." },
    { key: "email", label: "Email" },
    { key: "createdAt", label: "Created On" },
    { key: "statusTes", label: "Status Tes" },
    { key: "statusLogin", label: "Status Login" },
    { key: "updatedAt", label: "Last Logged in" },
  ];

  const isSaveDisabled =
    editData.email === originalData.email &&
    editData.statusTes === originalData.statusTes;

  return (
    <>
      <Col
        md={12}
        className="d-flex justify-content-between align-items-center mt-3  px-3"
      >
        <div className="d-flex">
          <h2>Manage User</h2>
        </div>
        <div className="d-flex ">
          <Button
            className="btn btn-success "
            onClick={() => setShowModal(true)}
          >
            <i className="bi bi-person-add me-2"></i>Add User
          </Button>
        </div>
      </Col>

      <Col md={12}>
        <TableAdmin
          columns={columns}
          data={combinedData}
          loading={loading} // Pass loading state
          handleEdit={handleEditUser}
          handleDelete={handleDeleteUser}
        />
      </Col>

      <ModalAdmin
        show={showModal}
        handleClose={handleCloseModal}
        handleSave={handleAddUser}
        title="Add New User"
        saveButtonText="Add User"
        formId="addUserForm"
      >
        <Form
          id="addUserForm"
          noValidate
          validated={validated}
          onSubmit={handleAddUser}
        >
          <Form.Group controlId="formEmail">
            <Form.Label>Email address</Form.Label>
            <Form.Control
          autoFocus
              required
              type="email"
              placeholder="Enter email"
              value={newEmail}
              onChange={(e) =>
                handleEmailChange(setNewEmail, setAddEmailError, e.target.value)
              }
              isInvalid={validated && addEmailError !== ""}
              isValid={
                validated && addEmailError === "" && isValidEmail(newEmail)
              }
            />
            <Form.Control.Feedback type="invalid">
              {addEmailError}
            </Form.Control.Feedback>
          </Form.Group>
        </Form>
      </ModalAdmin>

      <ModalAdmin
        show={showEditModal}
        handleClose={handleCloseModal}
        handleSave={handleSaveEdit}
        title="Edit User"
        saveButtonText="Save Change"
        isSaveDisabled={isSaveDisabled}
        formId="editUserForm"
      >
        <Form
          id="editUserForm"
          noValidate
          validated={validated}
          onSubmit={handleSaveEdit}
        >
          <Form.Group controlId="formEditEmail">
            <Form.Label>Email address</Form.Label>
            <Form.Control
              required
              type="email"
              placeholder="Enter email"
              value={editData.email}
              onChange={(e) =>
                handleEmailChange(
                  (email) => setEditData({ ...editData, email }),
                  setEditEmailError,
                  e.target.value
                )
              }
              isInvalid={validated && editEmailError !== ""}
              isValid={
                validated &&
                editEmailError === "" &&
                isValidEmail(editData.email)
              }
            />
            <Form.Control.Feedback type="invalid">
              {editEmailError}
            </Form.Control.Feedback>
          </Form.Group>
          <Form.Group controlId="formStatusTes">
            <Form.Label>Status Tes</Form.Label>
            {editData.statusTes === "N/A" ? (
              <Form.Control type="text" value="N/A" disabled />
            ) : (
              <Form.Control
                as="select"
                value={editData.statusTes}
                onChange={(e) =>
                  setEditData({ ...editData, statusTes: e.target.value })
                }
              >
                <option value="Pending Test">Pending Test</option>
                <option value="Completed Test">Completed Test</option>
              </Form.Control>
            )}
          </Form.Group>
        </Form>
      </ModalAdmin>
    </>
  );
};

export default ManageUser;
